import { useMutation } from "@tanstack/react-query";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import type { PasswordRequest } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import RecoveryLinkSent from "~/assets/icons/RecoveryLinkSent.svg?react";
import FormLabel from "~/components/login/formLabel/FormLabel";
import FormTitle from "~/components/login/formTitle/FormTitle";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import Input from "~/components/ui/input/Input";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { EMAIL_VALIDATION_REG_EX } from "~/helpers/helpers";
import labels from "~/helpers/labels";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./Login.module.scss";

const ForgotPasswordPage = () => {
  usePageTitle(PAGE_TITLES.resetPassword);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setError,
  } = useForm<PasswordRequest>();
  const { baseErrors, applyServerErrors } = useFormErrors(setError);

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: (data: PasswordRequest) =>
      apiClient.password.passwordCreate(data),
  });

  const onSubmit: SubmitHandler<PasswordRequest> = async (data) => {
    const result = await tryCatchParsed(mutateAsync(data));

    if (result.error) {
      applyServerErrors(result.error);
    }
  };

  const footer = (
    <div className={styles.footer}>
      <div className={styles.footerText}>{labels.KNOW_YOUR_PASSWORD}</div>
      <Link className={styles.footerLink} to={routes.auth.login()}>
        {labels.BACK_TO_LOGIN}
      </Link>
    </div>
  );

  return (
    <>
      <FormTitle
        title={isSuccess ? labels.RECOVERY_LINK_SENT : labels.PASSWORD_RESET}
      />
      {!isSuccess ? (
        <>
          <div className={styles.infoText}>
            {labels.ENTER_YOUR_EMAIL_CONFIRMATION}
          </div>
          <FormBaseErrors errors={baseErrors} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.inputsWrapper}>
              <FormLabel label={labels.EMAIL} />
              <Input
                disabled={isPending}
                errormessage={errors.email?.message ?? ""}
                {...register("email", {
                  required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
                  pattern: {
                    value: EMAIL_VALIDATION_REG_EX,
                    message: labels.INVALID_EMAIL,
                  },
                })}
              />
            </div>
            <Button
              fullWidth
              type="submit"
              disabled={isPending}
              withSpinner={isPending}
            >
              {labels.RESET_PASSWORD}
            </Button>
          </form>
        </>
      ) : (
        <>
          <div className={styles.infoText}>
            {labels.GET_RECOVERY_LINK_SENT_INFO(getValues("email"))}
          </div>
          <div className={styles.recoveryLinkSentIcon}>
            <RecoveryLinkSent />
          </div>
        </>
      )}
      {footer}
    </>
  );
};
export default ForgotPasswordPage;
